import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/store/admin/admin-auth-slice';
import type { AdminRole } from '@/admin/auth/admin-claims';
import { captureReturnTo } from '@/auth/return-to';

/**
 * Guard for admin routes.
 *
 * - Unauthenticated → redirect to /admin/login preserving the original path.
 * - Authenticated but role not in `allowedRoles` → redirect to /admin/403.
 * - Matching role → render children (via <Outlet />).
 */
export interface ProtectedRouteProps {
  allowedRoles?: AdminRole[];
}

export function ProtectedRoute({
  allowedRoles = ['superAdmin', 'orgAdmin'],
}: ProtectedRouteProps) {
  const { accessToken, role } = useAdminAuth();
  const location = useLocation();

  if (!accessToken) {
    // PROD-2366 — same defect as the PM guard: preserve the whole location,
    // not just the pathname, so an admin deep link survives the login round
    // trip with its query string and hash intact.
    return (
      <Navigate
        to="/admin/login"
        replace
        state={{ from: captureReturnTo(location) }}
      />
    );
  }
  if (!allowedRoles.includes(role)) {
    return <Navigate to="/admin/403" replace />;
  }
  return <Outlet />;
}
